import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { ApiResponse } from '../../../core/models/api-response.model';
import { Bank } from '../../../core/models/bank.model';
import { Currency } from '../../../core/models/currency.model';
import { CorporateContributorRequest, FundContributor, IndividualContributorRequest } from '../../../core/models/fund-contributor';
import { IndustryType } from '../../../core/models/industry-type';
import { FinancialDashboardService } from '../../../core/services/api-services/financial-dashboard.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { minimumAgeValidator } from './util/minimum-age-validator';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { InputLimitDirective } from '../../../shared/components/directive/input-limit.directive';
import { FormattedNumberInputComponent } from '../../../shared/components/formatted-number-input/formatted-number-input.component';

declare const bootstrap: any;

type ContributorType = 'individual' | 'company';

interface ContributorFormValue {
  contributorType: ContributorType;
  individual: {
    fullName: string;
    nic: string;
    gender: string;
    dob: string;
    email: string;
    mobileNumber: string;
    address: string;
    city: string;
    country: string;
    accountHolderName: string;
    bankId: number | null;
    accountNumber: string;
    contributionAmount: string;
    currency: number | null;
    costOfFund: string;
  };
  company: {
    companyName: string;
    vatNumber: string;
    industryId: null | number;
    website: string;
    representativeEmail: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    accountHolderName: string;
    accountNumber: string;
    bankId: number | null;
    contributionAmount: string;
    currency: number | null;
    costOfFund: string;
  };
}

interface ContributorRow {
  id: number;
  type: ContributorType;
  name: string;
  email: string;
  phoneNumber: string;
  country: string;
  contributionAmount: number;
  costOfFunds: number;
  currency: string;
  formData: ContributorFormValue;
}

@Component({
  selector: 'app-contributor-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, PaginatorComponent, BreadcrumbComponent, InputLimitDirective, FormattedNumberInputComponent],
  templateUrl: './contributor-management.component.html',
  styleUrl: './contributor-management.component.css'
})
export class ContributorManagementComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('contributorOffcanvasEl') offcanvasElementRef!: ElementRef<HTMLElement>;

  readonly contributorForm: FormGroup;
  readonly mode = signal<'add' | 'view' | 'edit'>('add');
  readonly editingContributorId = signal<number | null>(null);
  readonly searchText = signal('');
  readonly currencies = signal<Currency[]>([]);

  dropdownOpen = false;
  searchTerm = '';
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  readonly banks = signal<Bank[]>([]);
  readonly contributors = signal<ContributorRow[]>([]);

  readonly currencyCodeById = computed(() => {
    const map = new Map<number, string>();
    this.currencies().forEach((currency) => {
      if (currency.currencyId != null) {
        map.set(currency.currencyId, currency.isoCode);
      }
    });
    return map;
  });

  readonly bankNameById = computed(() => {
    const map = new Map<number, string>();
    this.banks().forEach((bank) => {
      if (bank.bankId != null) {
        map.set(bank.bankId, bank.bankName);
      }
    });
    return map;
  });

  readonly currencyIdByCode = computed(() => {
    const map = new Map<string, number>();
    this.currencies().forEach((currency) => {
      if (currency.isoCode && currency.currencyId != null) {
        map.set(currency.isoCode, currency.currencyId);
      }
    });
    return map;
  });

  private readonly defaultCurrencyCode: string = 'LKR';
  private readonly createdUserId = 0;

  private readonly fb = inject(FormBuilder);
  private readonly financialDashboardService = inject(FinancialDashboardService);
  private readonly toaster = inject(ToastrService);

  readonly filteredContributors = computed(() => {
    const term = this.searchText().toLowerCase().trim();
    const contributors = this.contributors();
    if (!term) {
      return contributors;
    }

    return contributors.filter(contributor =>
      Object.values(contributor).some(val =>
        String(val).toLowerCase().includes(term)
      )
    );
  });

  industries: Array<IndustryType> = [];

  // Pagination properties
  pagedContributors: any[] = [];

  ContributorsPage = 1;
  ContributorsPageSize = 10;
  ContributorsPageSizeOptions = [5, 10, 25, 50];

  dataSubscriber$!: Subscription;

  private originalFormValue: ContributorFormValue | null = null;

  filterBankSearchTerm = signal('');

  private pendingType: ContributorType | null = null;
  private previousType: ContributorType | null = null;
  confirmationMessage: string = '';

  isSubmitDisabled = false;

  constructor(private breadcrumbService: BreadcrumbService, protected authService: AuthService) {
    this.contributorForm = this.fb.group({
      contributorType: ['individual'],
      individual: this.fb.group({
        fullName: ['', [
          Validators.required,
          Validators.pattern(/^([A-Z][a-z]*[\.]?[\s]?)*([A-Z][a-z]*)$/),
          Validators.maxLength(150)
        ]],
        nic: ['', [
          Validators.required,
          Validators.pattern(/^(\d{9}[vVxX]|\d{12})$/), Validators.maxLength(12)
        ]],
        gender: ['', Validators.required],
        dob: ['', [
          Validators.required,
          minimumAgeValidator(18)
        ]],
        email: ['', [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          Validators.maxLength(254)
        ]],
        mobileNumber: ['', [
          Validators.required,
          Validators.pattern(/^((\+94|0)(70|71|72|74|75|76|77|78)\d{7})$/), Validators.maxLength(12)
        ]],
        address: ['', [Validators.required, Validators.maxLength(200)]],
        city: ['', [Validators.required, Validators.maxLength(168)]],
        country: ['', [Validators.required, Validators.maxLength(56)]],
        accountHolderName: ['', [Validators.required, Validators.pattern(/^([A-Z][a-z]*[\.]?[\s]?)*([A-Z][a-z]*)$/), Validators.maxLength(150)]],
        bankId: [null, Validators.required],
        accountNumber: ['', [
          Validators.required,
          Validators.pattern(/^[0-9]{8,18}$/),
          Validators.maxLength(18)
        ]],
        contributionAmount: ['', [
          Validators.required,
          Validators.min(0),
          Validators.max(999999999999.99),
          // Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/)
        ]],
        currency: [null, Validators.required],
        costOfFund: ['', [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/)
        ]]
      }),
      company: this.fb.group({
        companyName: ['', [
          Validators.required,
          Validators.pattern(/^[A-Z][A-Za-z\s().&-]*$/),
          Validators.maxLength(150)
        ]],
        vatNumber: ['', [
          Validators.required,
          Validators.pattern(/^([0-9]{9}[Vv]|[0-9]{15}|[0-9]{9}-7000)$/),
          Validators.minLength(9),
          Validators.maxLength(15)
        ]],
        industryId: [null, Validators.required],
        website: [''],
        representativeEmail: ['', [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
          Validators.maxLength(254)
        ]],
        phone: ['', [
          Validators.required,
          Validators.pattern(/^(\+94(70|71|72|74|75|76|77|78|11|21|23|24|25|26|27|31|32|33|34|35|36|37|38|41|45|47|51|52|54|55|57|63|65|66|67|81|91)\d{7}|0(70|71|72|74|75|76|77|78|11|21|23|24|25|26|27|31|32|33|34|35|36|37|38|41|45|47|51|52|54|55|57|63|65|66|67|81|91)\d{7})$/),
          Validators.maxLength(12)
        ]],
        address: ['', [Validators.required, Validators.maxLength(200)]],
        city: ['', [Validators.required, Validators.maxLength(168)]],
        country: ['', [Validators.required, Validators.maxLength(56)]],
        accountHolderName: ['', [Validators.required, Validators.pattern(/^(([A-Z][a-z]*[\.]?[\s]?)*([A-Z][a-z]*)|([A-Z][A-Za-z\s().&-]*))$/), Validators.maxLength(150)]],
        accountNumber: ['', [
          Validators.required,
          Validators.pattern(/^[0-9]{8,18}$/)
        ]],
        bankId: [null, Validators.required],
        contributionAmount: ['', [
          Validators.required,
          Validators.min(0),
          Validators.max(999999999999.99),
          // Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/)
        ]],
        currency: [null, Validators.required],
        costOfFund: ['', [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
          Validators.pattern(/^\d{1,3}(\.\d{1,2})?$/)
        ]]
      })
    });

    this.setContributorTypeControls('individual');

    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Fund Management', icon: 'bi bi-cash-stack' },
      { label: 'Contributor Management', icon: 'bi bi-person-check' }
    ]);

    this.loadBanks();
    this.loadIndustries();
    this.loadCurrencies(() => this.loadFundContributors());
  }

  loadBanks(): void {
    this.dataSubscriber$.add(
      this.financialDashboardService.getAllBankDetails().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.banks.set(response.data);
          }
        },
        error: (error) => {
          console.error('Error loading banks:', error);
        }
      })
    );
  }

  // =====================================//
  // Filtered banks based on search term //
  // =====================================//
  filteredBanks = computed(() => {
    const term = this.filterBankSearchTerm().toLowerCase().trim();

    if (!term) {
      return this.banks();
    }

    return this.banks().filter(bank =>
      bank.bankName.toLowerCase().includes(term)
    );
  });

  // Open dropdown
  openDropdown() {
    this.dropdownOpen = true;
    this.filterBankSearchTerm.set(''); // Clear search when opening
  }

  // Handle search input changes
  onFilterBankSearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.filterBankSearchTerm.set(input.value);
    this.dropdownOpen = true;
  }

  // Select a bank from dropdown
  selectBank(bank: any) {
    if (!bank) {
      return;
    }

    if (this.getContributorType() === 'individual') {
      this.contributorForm.get('individual.bankId')?.setValue(bank.bankId);
      this.contributorForm.get('individual.bankId')?.markAsTouched();
      this.contributorForm.get('individual.bankId')?.markAsDirty();
      return;
    } else {
      this.contributorForm.get('company.bankId')?.setValue(bank.bankId);
      this.contributorForm.get('company.bankId')?.markAsTouched();
      this.contributorForm.get('company.bankId')?.markAsDirty();
    }

    this.filterBankSearchTerm.set('');
    this.dropdownOpen = false;
  }

  // Get selected bank name for display
  getSelectedBankName(): string {
    let selectedId: number | null = null;
    if (this.getContributorType() === 'individual') {
      selectedId = this.contributorForm.get('individual.bankId')?.value;
    } else if (this.getContributorType() === 'company') {
      selectedId = this.contributorForm.get('company.bankId')?.value;
    } else {
      return '';
    }

    if (!selectedId) {
      return '';
    }

    const selectedBank = this.banks().find(b => b.bankId === selectedId);
    return selectedBank ? selectedBank.bankName : '';
  }

  // Handle blur (close dropdown if click outside)
  onBlur() {
    setTimeout(() => {
      this.dropdownOpen = false;
      // Only clear search term if nothing selected or after selection
      if (!this.contributorForm.get('company.bankId')?.value) {
        this.filterBankSearchTerm.set('');
      }
    }, 200);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-dropdown')) {
      this.dropdownOpen = false;
      this.filterBankSearchTerm.set('');
    }
  }

  // =======================//
  // End of searchable dropdown //
  // =======================//

  loadCurrencies(onComplete?: () => void): void {
    this.dataSubscriber$.add(
      this.financialDashboardService.getAllCurrencies().subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.currencies.set(response.data);
          }
        },
        error: (error) => {
          console.error('Error loading currencies:', error);
          onComplete?.();
        },
        complete: () => onComplete?.()
      })
    );
  }

  loadIndustries(): void {
    this.dataSubscriber$.add(
      this.financialDashboardService.getAllIndustries().subscribe({
        next: (response) => {
          if (response.success && Array.isArray(response.data)) {
            this.industries = response.data;
          }
        },
        error: (error) => {
          console.error('Error loading industries:', error);
        }
      })
    )
  }

  loadFundContributors(): void {
    this.dataSubscriber$.add(
      this.financialDashboardService.getFundContributorDetails().subscribe({
        next: (response) => {
          if (response.success && Array.isArray(response.data)) {
            const mappedContributors = response.data.map((contributor) =>
              this.mapContributorResponse(contributor)
            );
            this.contributors.set(mappedContributors);
          } else {
            this.contributors.set([]);
          }
        },
        error: (error) => {
          console.error('Error loading fund contributors:', error);
          this.contributors.set([]);
        }
      })
    );
  }

  onSearchChange(value: string): void {
    this.searchText.set(value ?? '');
  }

  openAddContributorForm(): void {
    this.mode.set('add');
    this.editingContributorId.set(null);
    this.contributorForm.enable();
    const defaultFormValue = this.createEmptyContributorFormValue('individual');
    this.contributorForm.reset({
      contributorType: defaultFormValue.contributorType,
      individual: defaultFormValue.individual,
      company: defaultFormValue.company
    });
    this.setContributorTypeControls('individual');
  }

  openViewContributorForm(contributor: ContributorRow): void {
    this.mode.set('view');
    this.editingContributorId.set(contributor.id);

    // Enable first so validators can run
    // this.contributorForm.enable();

    // Fill data
    this.contributorForm.reset(contributor.formData);

    // Apply dynamic validators if any
    this.setContributorTypeControls(contributor.formData.contributorType);

    // Force validation
    this.contributorForm.updateValueAndValidity({ emitEvent: false });

    // Mark all fields touched so errors appear
    this.contributorForm.markAllAsTouched();

    // Now disable for view mode
    this.contributorForm.disable({ emitEvent: false });

    // Store original value
    this.originalFormValue =
      this.contributorForm.getRawValue() as ContributorFormValue;
  }

  openEditContributorForm(): void {
    if (this.mode() !== 'view') {
      return;
    }

    this.mode.set('edit');

    this.contributorForm.enable(); // enable all controls

    // Disable contributorType control
    this.contributorForm.get('contributorType')?.disable();

    this.setContributorTypeControls(
      this.contributorForm.get('contributorType')?.value
    );

    const errors = this.getAllFormErrors(this.contributorForm);
  }

  // Get all errors in a structured format
  getAllFormErrors(form: FormGroup): any {
    const errors: any = {};

    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);

      if (control instanceof FormGroup) {
        const nestedErrors = this.getAllFormErrors(control);
        if (Object.keys(nestedErrors).length > 0) {
          errors[key] = nestedErrors;
        }
      } else {
        if (control?.errors) {
          errors[key] = control.errors;
        }
      }
    });

    return errors;
  }

  cancelEdit(): void {
    this.mode.set('view');
    this.contributorForm.disable();
    const contributorId = this.editingContributorId();
    if (contributorId) {
      const contributor = this.contributors().find(c => c.id === contributorId);
      if (contributor) {
        this.openViewContributorForm(contributor);
      }
    }
  }

  //========================================================//
  // Get Confirmation from user before form chnage //
  //========================================================//
  private formHasData(formGroup: FormGroup): boolean {
    const controls = formGroup.controls;

    for (const key in controls) {

      if (key === 'currency') {
        continue;
      }

      const value = controls[key].value;

      if (value === null || value === undefined || value === '') {
        continue;
      }

      // Arrays (multi-selects, checklists)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          return true;
        }
        continue;
      }

      // Numbers (ignore 0 if it's default)
      if (typeof value === 'number') {
        if (value !== 0) {
          return true;
        }
        continue;
      }

      // Objects (e.g. dropdown objects)
      if (typeof value === 'object') {
        if (Object.keys(value).length > 0) {
          return true;
        }
        continue;
      }

      // Strings
      return true;
    }

    return false;
  }

  private showConfirmationDialog(newType: ContributorType, currentType: ContributorType): void {
    const typeName = currentType === 'individual' ? 'company' : 'individual';

    this.confirmationMessage = `Changing contributor type will clear all ${typeName} form data. Are you sure you want to clear?`;
    this.pendingType = newType;
    this.previousType = currentType;
    this.isSubmitDisabled = true;

    const modal = new bootstrap.Modal(document.getElementById('contributorTypeChangeModal'));
    modal.show();
  }

  private changeContributorType(type: ContributorType): void {
    this.contributorForm.get('contributorType')?.setValue(type);
    this.setContributorTypeControls(type);
  }

  onConfirmTypeChange(): void {
    if (!this.pendingType) return;

    this.clearForm();
    this.changeContributorType(this.pendingType);

    this.pendingType = null;
    this.previousType = null;

    const modalElement = document.getElementById('contributorTypeChangeModal');
    bootstrap.Modal.getInstance(modalElement!)?.hide();
  }

  onCancelTypeChange(): void {
    this.contributorForm.get('contributorType')?.setValue(this.previousType, { emitEvent: false });
    this.contributorForm.get('contributorType')?.setValue(this.previousType);
    this.setContributorTypeControls(this.previousType === 'company' ? 'individual' : 'company');

    this.pendingType = null;
    this.previousType = null;
    const modalElement = document.getElementById('contributorTypeChangeModal');
    const modal = bootstrap.Modal.getInstance(modalElement);
    modal?.hide();
  }

  onContributorTypeChange(type: ContributorType): void {

    this.previousType = type === 'company' ? 'individual' : 'company';

    if (this.mode() === 'view') {
      return;
    }

    const currentType = this.contributorForm.get('contributorType')?.value;

    // If same type selected → do nothing
    if (currentType === this.pendingType) {
      return;
    }

    const previousFormGroup = this.contributorForm.get(this.previousType) as FormGroup;
    const hasData = this.formHasData(previousFormGroup);
    console.log(hasData);

    if (hasData) {
      this.showConfirmationDialog(type, currentType);
      return;
    } else {
      this.changeContributorType(type);
    }
  }


  //========================================================//

  // onContributorTypeChange(type: ContributorType): void {
  //   if (this.mode() === 'view') {
  //     return;
  //   }
  //   this.contributorForm.get('contributorType')?.setValue(type);
  //   this.setContributorTypeControls(type);
  // }

  //==============================================================//

  isSectionActive(type: ContributorType): boolean {
    return this.contributorForm.get('contributorType')?.value === type;
  }

  private setContributorTypeControls(type: ContributorType): void {
    if (type === 'individual') {
      this.contributorForm.get('individual')?.enable({ emitEvent: false });
      this.contributorForm.get('company')?.disable({ emitEvent: false });
      this.isSubmitDisabled = type !== this.contributorForm.get('contributorType')?.value;
    } else {
      this.contributorForm.get('company')?.enable({ emitEvent: false });
      this.contributorForm.get('individual')?.disable({ emitEvent: false });
      this.isSubmitDisabled = type !== this.contributorForm.get('contributorType')?.value;
    }
  }

  getContributorType(): ContributorType {
    return this.contributorForm.get('contributorType')?.value;
  }

  private createEmptyContributorFormValue(type: ContributorType): ContributorFormValue {
    const defaultCurrencyId = this.getDefaultCurrencyId();
    return {
      contributorType: type,
      individual: {
        fullName: '',
        nic: '',
        gender: '',
        dob: '',
        email: '',
        mobileNumber: '',
        address: '',
        city: '',
        country: '',
        accountHolderName: '',
        bankId: null,
        accountNumber: '',
        contributionAmount: '',
        currency: defaultCurrencyId,
        costOfFund: ''
      },
      company: {
        companyName: '',
        vatNumber: '',
        industryId: null,
        website: '',
        representativeEmail: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        accountHolderName: '',
        accountNumber: '',
        bankId: null,
        contributionAmount: '',
        currency: defaultCurrencyId,
        costOfFund: ''
      }
    };
  }

  private mapContributorResponse(contributor: FundContributor): ContributorRow {
    const type = this.mapContributorType(contributor.contributorTypeId);
    const formData = this.buildContributorFormValue(contributor, type);
    const individualSection = formData.individual;
    const companySection = formData.company;
    const activeSection = type === 'individual' ? individualSection : companySection;
    const currencyCode =
      this.getCurrencyCodeById(activeSection.currency) ?? this.defaultCurrencyCode;

    return {
      id: contributor.contributorId ?? 0,
      type,
      name: (type === 'individual' ? individualSection.fullName : companySection.companyName) ?? '',
      email: (type === 'individual' ? individualSection.email : companySection.representativeEmail) ?? '',
      phoneNumber: (type === 'individual' ? individualSection.mobileNumber : companySection.phone) ?? '',
      country: activeSection.country ?? '',
      contributionAmount: this.toNumber(activeSection.contributionAmount),
      costOfFunds: this.toNumber(activeSection.costOfFund),
      currency: currencyCode,
      formData
    };
  }

  private buildContributorFormValue(
    contributor: FundContributor,
    type: ContributorType
  ): ContributorFormValue {
    const base = this.createEmptyContributorFormValue(type);
    const currencyId = contributor.currencyId ?? base.individual.currency;
    const contributionAmount = this.toStringValue(contributor.contributionAmount);
    const costOfFund = this.toStringValue(contributor.costOfFundPercent);

    const individual = {
      ...base.individual,
      fullName: type === 'individual' ? contributor.legalName ?? base.individual.fullName : base.individual.fullName,
      nic: contributor.nic ?? base.individual.nic,
      dob: contributor.dateOfBirth ? contributor.dateOfBirth.substring(0, 10) : base.individual.dob,
      gender: contributor.gender ?? base.individual.gender,
      email: contributor.email ?? base.individual.email,
      mobileNumber: contributor.mobileNumber ?? contributor.representativePhone ?? base.individual.mobileNumber,
      address: contributor.address ?? base.individual.address,
      city: contributor.city ?? base.individual.city,
      country: contributor.country ?? base.individual.country,
      accountHolderName: contributor.accountHolderName ?? base.individual.accountHolderName,
      bankId: contributor.bankId ?? base.individual.bankId,
      accountNumber: contributor.accountNumber ?? base.individual.accountNumber,
      contributionAmount: contributionAmount || base.individual.contributionAmount,
      currency: currencyId,
      costOfFund: costOfFund || base.individual.costOfFund
    };

    const company = {
      ...base.company,
      companyName: type === 'company' ? contributor.legalName ?? base.company.companyName : base.company.companyName,
      vatNumber: contributor.vatNumber ?? base.company.vatNumber,
      industryId: contributor.industryId ?? base.company.industryId,
      website: contributor.companyWebsite ?? base.company.website,
      representativeEmail: contributor.representativeEmail ?? contributor.email ?? base.company.representativeEmail,
      phone: contributor.representativePhone ?? contributor.mobileNumber ?? base.company.phone,
      address: contributor.address ?? base.company.address,
      city: contributor.city ?? base.company.city,
      country: contributor.country ?? base.company.country,
      accountHolderName: contributor.accountHolderName ?? base.company.accountHolderName,
      bankId: contributor.bankId ?? base.company.bankId,
      accountNumber: contributor.accountNumber ?? base.company.accountNumber,
      contributionAmount: contributionAmount || base.company.contributionAmount,
      currency: currencyId,
      costOfFund: costOfFund || base.company.costOfFund
    };

    return {
      contributorType: type,
      individual,
      company
    };
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toStringValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  private getCurrencyCodeById(id: number | null): string | undefined {
    if (id == null) {
      return undefined;
    }
    return this.currencyCodeById().get(id);
  }

  private getDefaultCurrencyId(): number | null {
    return this.getCurrencyIdByIso(this.defaultCurrencyCode);
  }

  private mapContributorType(typeId: number | null): ContributorType {
    return typeId === 2 ? 'company' : 'individual';
  }

  submitContributor(): void {
    if (this.mode() === 'view') {
      return;
    }

    if (this.contributorForm.invalid) {
      this.contributorForm.markAllAsTouched();
      return;
    }

    const formValue = this.contributorForm.getRawValue() as ContributorFormValue;
    const type = formValue.contributorType;
    const isEditMode = this.mode() === 'edit';
    const contributorId = isEditMode ? this.editingContributorId() : null;

    // Check if form has been modified in edit mode
    if (isEditMode) {
      if (!this.contributorForm.dirty) {
        this.toaster.info('No changes detected to update.', 'Info');
        return;
      }

      // Optional: Deep comparison
      if (this.originalFormValue && this.areValuesEqual(formValue, this.originalFormValue)) {
        this.toaster.info('No changes detected to update.', 'Info');
        return;
      }
    }

    const request =
      type === 'company'
        ? this.buildCorporateContributorRequest(formValue, contributorId)
        : this.buildIndividualContributorRequest(formValue, contributorId);

    let request$: Observable<ApiResponse<boolean>>;

    if (isEditMode) {
      // console.log('Update Contributor Payload:', request);
      request$ =
        type === 'company'
          ? this.financialDashboardService.updateCorporateContributor(request as CorporateContributorRequest)
          : this.financialDashboardService.updateIndividualContributor(request as IndividualContributorRequest);
    } else {
      request$ =
        type === 'company'
          ? this.financialDashboardService.addCorporateContributor(request as CorporateContributorRequest)
          : this.financialDashboardService.addIndividualContributor(request as IndividualContributorRequest);
    }

    this.dataSubscriber$.add(
      request$.subscribe({
        next: (response) => {
          if (response.success) {
            const message = isEditMode
              ? (response.message || 'Contributor updated successfully.')
              : (response.message || 'Contributor saved successfully.');
            this.toaster.success(message, 'Success');

            if (isEditMode) {
              this.financialDashboardService.getFundContributorDetails().subscribe({
                next: (reloadResponse) => {
                  if (reloadResponse.success && Array.isArray(reloadResponse.data)) {
                    const mappedContributors = reloadResponse.data.map((contributor) =>
                      this.mapContributorResponse(contributor)
                    );
                    this.contributors.set(mappedContributors);
                  }
                  this.clearForm();
                  this.closeOffcanvas();
                  this.openAddContributorForm();
                },
                error: () => {
                  this.closeOffcanvas();
                  this.openAddContributorForm();
                }
              });
            } else {
              this.loadFundContributors();
              this.closeOffcanvas();
              this.openAddContributorForm();
            }
            this.clearForm();
          } else {
            this.toaster.error(response.message || 'Failed to save contributor.', 'Error');
          }
        },
        error: (error) => {
          console.error('Error saving contributor:', error);
          this.toaster.error(error?.error.message || error?.message || 'Error saving contributor. Please try again.', 'Error');
        }
      })
    );
  }

  private buildCorporateContributorRequest(formValue: ContributorFormValue, contributorId: number | null = null): CorporateContributorRequest {
    const company = formValue.company;
    return {
      contributorId: contributorId,
      contributorTypeId: 2,
      legalName: company.companyName || null,
      vatNumber: company.vatNumber || null,
      companyRegNumber: null,
      industryId: this.toNumberOrNull(company.industryId),
      companyWebsite: company.website || null,
      representativeEmail: company.representativeEmail || null,
      representativePhone: company.phone || null,
      country: company.country || null,
      address: company.address || null,
      city: company.city || null,
      bankId: this.toNumberOrNull(company.bankId),
      accountHolderName: company.accountHolderName || null,
      accountNumber: company.accountNumber || null,
      contributionAmount: this.toNumberOrNull(company.contributionAmount),
      currencyId: this.toNumberOrNull(company.currency),
      costOfFundPercent: this.toNumberOrNull(company.costOfFund),
      createdUser: this.createdUserId
    };
  }

  private buildIndividualContributorRequest(formValue: ContributorFormValue, contributorId: number | null = null): IndividualContributorRequest {
    const individual = formValue.individual;
    return {
      contributorId: contributorId,
      contributorTypeId: 1,
      legalName: individual.fullName || null,
      nic: individual.nic || null,
      dateOfBirth: this.formatDateToIso(individual.dob),
      gender: individual.gender || null,
      email: individual.email || null,
      mobileNumber: individual.mobileNumber || null,
      address: individual.address || null,
      city: individual.city || null,
      country: individual.country || null,
      bankId: this.toNumberOrNull(individual.bankId),
      accountHolderName: individual.accountHolderName || null,
      accountNumber: individual.accountNumber || null,
      contributionAmount: this.toNumberOrNull(individual.contributionAmount),
      currencyId: this.toNumberOrNull(individual.currency),
      costOfFundPercent: this.toNumberOrNull(individual.costOfFund),
      createdUser: this.createdUserId
    };
  }

  private formatDateToIso(dateValue: string | null | undefined): string | null {
    if (!dateValue) {
      return null;
    }
    return `${dateValue}T00:00:00.000Z`;
  }

  private getCurrencyIdByIso(code: string | null | undefined): number | null {
    if (!code) {
      return null;
    }
    const id = this.currencyIdByCode().get(code);
    return typeof id === 'number' ? id : null;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private closeOffcanvas(): void {
    const offcanvasEl = this.offcanvasElementRef?.nativeElement;
    if (!offcanvasEl || typeof bootstrap === 'undefined') {
      return;
    }
    const instance = bootstrap.Offcanvas.getInstance(offcanvasEl) || new bootstrap.Offcanvas(offcanvasEl);
    instance.hide();
  }

  private updateContributorsPage(): void {
    if (this.ContributorsPage < 1) this.ContributorsPage = 1;
    const start = (this.ContributorsPage - 1) * this.ContributorsPageSize;
    this.pagedContributors = this.filteredContributors().slice(start, start + this.ContributorsPageSize);
  }

  private resetContributorsPagination(): void {
    this.ContributorsPage = 1;
    this.updateContributorsPage();
  }


  onContributorsPageChange(paginatedData: any[]): void {
    this.pagedContributors = paginatedData;
  }

  clearForm(): void {
    this.contributorForm.reset({
      contributorType: this.getContributorType() === 'individual' ? 'individual' : 'company',
      individual: {
        gender: '',
        bankId: null,
        currency: null,
      },
      company: {
        industry: null,
        bankId: null,
        currency: null,
      }
    });
  }

  // Helper method for deep comparison
  private areValuesEqual(val1: any, val2: any): boolean {
    return JSON.stringify(val1) === JSON.stringify(val2);
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}